/* eslint-disable no-restricted-syntax */
import { Divider, Flex, Text, Color } from '@brokerbay/elements-web';
import { List } from 'components';
import moment from 'moment';
import React, {
  useContext,
  useLayoutEffect,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import styled from 'styled-components';
import { buildCssLengthProp, getTimezoneCode } from 'utils';
import AppointmentBookingInternalContext from '../../../AppointmentBookingInternalContext';
import BookingTimeSelectContext from '../BookingTimeSelectContext';
import DurationPopover from '../DurationPopover';
import type { TimeSlotData } from '../types';
import TimeSelectorCell from './TimeSelectorCell';
import { getTimeSlotIdByMinute } from './helpers';
import NoAvailableTimeslotsBanner from './NoAvailableTimeslotsBanner';

const TimeSelectorStep: React.FC = () => {
  const {
    listing,
    offerDetails,
    isAdminWithAccess,
    isListingAgent,
    requiresBuyerAgreement,
    partialAppointmentConfig,
  } = useContext(AppointmentBookingInternalContext);
  const {
    recordShowingData,
    setRecordShowingData,
    appointmentDate,
    appointmentTime,
    setAppointmentTime,
    duration,
    selectedDateStates,
    setIsDurationPopoverOpen,
    unavailableTimeSlots,
    selectedTimeStatus,
    appointmentTimeSlotInfoMap,
    earliestBookingDateAllowed,
  } = useContext(BookingTimeSelectContext);

  const [expandedTimeSlot, setExpandedTimeSlot] = useState<number | null>(null);
  const [timeSlotListHeight, setTimeSlotListHeight] = useState(250);
  const timeSlotContainerRef = useRef<HTMLDivElement>(null);

  const {
    isRecordShowing: isRecordMode,
    shouldShowWarning: shouldShowRecordWarning,
  } = recordShowingData;
  const { isToday, isPast, isOfferDate, isOpenHouseDate } = selectedDateStates;
  const now = moment();
  const maxPastTimeSlotMinute = isToday
    ? now.hour() * 60 + Math.floor((now.minute() / 15) * 15)
    : null;

  useLayoutEffect(() => {
    // Stretch timeslots to fill available height
    if (!timeSlotContainerRef.current) return;
    if (timeSlotContainerRef.current) {
      let buffer = 16;
      if (requiresBuyerAgreement) {
        // Hard-coded height of the buyer agreement checkbox
        buffer += 108;
      }
      const containerTop =
        timeSlotContainerRef.current.getBoundingClientRect().top;
      setTimeSlotListHeight(
        Math.min(Math.max(0, window.innerHeight - buffer - containerTop), 600),
      );
    }
  }, [requiresBuyerAgreement]);

  useEffect(() => setExpandedTimeSlot(null), [appointmentDate]);

  const offerTimeSlotMinute = useMemo(() => {
    if (!isOfferDate) return null;
    const offerDate = new Date(offerDetails!.offerSettings.offerDate);
    return (
      offerDate.getHours() * 60 + Math.floor((offerDate.getMinutes() / 15) * 15)
    );
  }, [isOfferDate, offerDetails]);

  /** [[OH start 1, OH end1], [OH start 2, ...]] */
  const openHouseTimeSlotMinuteRanges = useMemo(() => {
    if (!isOpenHouseDate) return [];
    const appointmentMoment = moment(appointmentDate);
    return listing!.openHouses.reduce<[number, number][]>(
      (acc, { timeFrom, timeTo }) => {
        const timeFromMoment = moment(timeFrom);
        if (!appointmentMoment.isSame(timeFromMoment, 'date')) return acc;
        const timeToMoment = moment(timeTo);
        acc.push([
          timeFromMoment.hours() * 60 +
            Math.floor((timeFromMoment.minutes() / 15) * 15),
          timeToMoment.hours() * 60 +
            Math.floor((timeToMoment.minutes() / 15) * 15),
        ]);
        return acc;
      },
      [],
    );
  }, [appointmentDate, isOpenHouseDate, listing]);

  const timeSlots = useMemo(() => {
    const result = [];
    for (const meridian of ['AM', 'PM']) {
      for (let hour = 0; hour < 12; hour++) {
        for (const minute of ['00', '15', '30', '45']) {
          const timeSlotData: TimeSlotData = {
            minute: +minute + hour * 60 + (meridian === 'AM' ? 0 : 60 * 12),
            text: `${hour || 12}:${minute} ${meridian}`,
          };
          if (unavailableTimeSlots[timeSlotData.minute]) {
            timeSlotData.isUnavailable = true;
          }
          if (
            isPast ||
            (maxPastTimeSlotMinute !== null &&
              maxPastTimeSlotMinute > timeSlotData.minute)
          ) {
            timeSlotData.isPast = true;
          }
          if (offerTimeSlotMinute === timeSlotData.minute) {
            timeSlotData.isOffer = true;
            timeSlotData.text += ' (Offer Pres)';
          }
          if (openHouseTimeSlotMinuteRanges.length) {
            if (
              openHouseTimeSlotMinuteRanges.some(
                ([startMinute]) => startMinute === timeSlotData.minute,
              )
            ) {
              timeSlotData.isOpenHouse = true;
              timeSlotData.text += ' (Open House Start)';
            }
            if (
              openHouseTimeSlotMinuteRanges.some(
                ([s, endMinute]) => endMinute === timeSlotData.minute,
              )
            ) {
              timeSlotData.isOpenHouse = true;
              timeSlotData.text += ' (Open House End)';
            }
            if (
              !timeSlotData.isOpenHouse &&
              openHouseTimeSlotMinuteRanges.some(
                ([startMinute, endMinute]) =>
                  endMinute >= timeSlotData.minute &&
                  timeSlotData.minute >= startMinute,
              )
            ) {
              timeSlotData.isOpenHouse = true;
            }
          }
          if (appointmentTimeSlotInfoMap?.[timeSlotData.minute]?.length) {
            timeSlotData.appointments =
              appointmentTimeSlotInfoMap[timeSlotData.minute];
            timeSlotData.expanded = expandedTimeSlot === timeSlotData.minute;
          }

          if (appointmentTime !== null) {
            if (appointmentTime === timeSlotData.minute) {
              let endHour = hour + Math.floor(duration / 60);
              let endMinute = +minute + (duration % 60);
              let endMeridian = meridian;
              if (endMinute >= 60) {
                endHour += 1;
                endMinute %= 60;
              }
              if (endHour >= 12) {
                endHour %= 12;
                endMeridian = meridian === 'AM' ? 'PM' : 'AM';
              }
              timeSlotData.text += ` - ${endHour || 12}:${String(endMinute).padStart(2, '0')} ${endMeridian}`;
              timeSlotData.selected = 'start';
            } else if (
              appointmentTime < timeSlotData.minute &&
              appointmentTime + duration >= timeSlotData.minute
            ) {
              timeSlotData.selected = 'nonStart';
            }
          }
          result.push(timeSlotData);
        }
      }
    }
    return result;
  }, [
    unavailableTimeSlots,
    isPast,
    maxPastTimeSlotMinute,
    offerTimeSlotMinute,
    openHouseTimeSlotMinuteRanges,
    appointmentTimeSlotInfoMap,
    appointmentTime,
    expandedTimeSlot,
    duration,
  ]);

  const firstActiveTimeSlot = useMemo(() => timeSlots.find(t => !t.isUnavailable)?.minute, [timeSlots]);

  useLayoutEffect(() => {
    if (!timeSlotContainerRef.current || !firstActiveTimeSlot) return;
    const timeSlotList = timeSlotContainerRef.current.children[0];
    const timeSlot = timeSlotList.querySelector<HTMLLIElement>(
      `#${getTimeSlotIdByMinute(firstActiveTimeSlot)}`,
    );
    if (timeSlot) {
      timeSlotList.scroll({ top: timeSlot.offsetTop, behavior: 'smooth' });
    }
  }, [firstActiveTimeSlot, appointmentDate]);

  const handleTimeSlotClick = (timeSlot: TimeSlotData) => {
    if (timeSlot.isPast) {
      if (shouldShowRecordWarning) {
        setRecordShowingData(prev => ({
          ...prev,
          isModalOpen: true,
          onOk: () => {
            setAppointmentTime(timeSlot.minute);
            setIsDurationPopoverOpen(true);
          },
        }));
        return;
      }
      setRecordShowingData(prev => ({
        ...prev,
        isRecordShowing: true,
      }));
    }
    if (isRecordMode && !timeSlot.isPast) {
      setRecordShowingData(prev => ({ ...prev, isRecordShowing: false }));
    }
    setAppointmentTime(timeSlot.minute);
    setIsDurationPopoverOpen(true);
  };

  const handleCounterClick = (timeSlot: TimeSlotData) => {
    setExpandedTimeSlot(prev =>
      prev === timeSlot.minute ? null : timeSlot.minute,
    );
  };

  const isComingSoonPreDate =
    listing?.status === 'Coming Soon' &&
    moment(appointmentDate).isBefore(earliestBookingDateAllowed);

  return (
    <StyledContainer vertical gap="middle">
      <Flex justify="center">
        Times reflect the listing&apos;s timezone (
        {getTimezoneCode(listing!.timezone)})
      </Flex>
      <Divider marginSize="none" />

      {isComingSoonPreDate && !(isAdminWithAccess || isListingAgent) ? (
        <Flex justify="center">
          <NoAvailableTimeslotsBanner date={appointmentDate} />
        </Flex>
      ) : (
        <>
          {selectedTimeStatus === 'Restricted' && (
            <DangerText dataCy="restricted-time-text">
              RESTRICTED TIME SELECTED
            </DangerText>
          )}

          {selectedTimeStatus !== 'Restricted' &&
            (appointmentTime ?? -1) >= 0 &&
            partialAppointmentConfig!.autoConfirm && (
              <SuccessText dataCy="autoconfirm-time-text">
                AUTO-CONFIRM TIME SELECTED
              </SuccessText>
            )}

          {selectedTimeStatus === 'AdvancedAutoconfirm' && (
            <SuccessText dataCy="autoconfirm-time-text">
              AUTO-CONFIRM TIME SELECTED
            </SuccessText>
          )}

          <div ref={timeSlotContainerRef}>
            <DurationPopover>
              <StyledList
                $height={timeSlotListHeight}
                bordered
                data-cy="appointmentBookinginPersonTab-time-slots"
                dataSource={timeSlots}
                renderItem={(timeSlot: (typeof timeSlots)[number]) => (
                  <TimeSelectorCell
                    onClick={handleTimeSlotClick}
                    onCounterClick={handleCounterClick}
                    timeSlot={timeSlot}
                    shouldShowRecordWarning={shouldShowRecordWarning}
                    isListingAgent={isListingAgent}
                    isAdminWithAccess={isAdminWithAccess}
                  />
                )}
              />
            </DurationPopover>
          </div>
        </>
      )}
    </StyledContainer>
  );
};

const StyledContainer = styled(Flex)`
  flex-grow: 1;
`;

const StyledList = styled(List)<{ $height: number }>`
  ${({ $height }) => buildCssLengthProp('height', $height)};
  overflow-y: auto;
  min-height: 250px;
  max-width: 400px;
  margin: 0 auto;
`;

export const DangerText = styled(Text)`
  color: ${Color.JANEMBA};
`;

export const SuccessText = styled(Text)`
  color: ${Color.ROSHI};
`;

export default TimeSelectorStep;
